import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mic2, Music2, Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import SetupWizard from "@/components/recstudio/setup/SetupWizard";
import SetupSummary from "@/components/recstudio/SetupSummary";
import { createRecSession, deleteRecSession, listRecSessions, loadSetup } from "@/lib/recStudioApi";
import { safeApiError } from "@/lib/safeErrorText";
import { fmtAgo } from "@/lib/studioApi";

function SessionCard({ s, onOpen, onDelete }) {
  const [sure, setSure] = useState(false);
  return (
    <li data-testid={`rec-session-${s.id}`} className="group flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-4 transition-colors duration-200 hover:bg-white/[0.05]">
      <button type="button" data-testid={`rec-session-open-${s.id}`} onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-rose-500 to-red-600"><Mic2 className="h-5 w-5" /></span>
        <span className="min-w-0">
          <span className="block truncate font-display text-[15px] font-semibold">{s.title}</span>
          <span className="block truncate text-[12px] text-white/50">
            <Music2 className="mr-1 inline h-3 w-3" />{s.instrumental || "No music yet"} · {s.tracks} track{s.tracks === 1 ? "" : "s"} · {s.takes} take{s.takes === 1 ? "" : "s"} · {fmtAgo(s.updated_at)}
          </span>
        </span>
      </button>
      <button type="button" data-testid={`rec-session-delete-${s.id}`} onClick={() => (sure ? onDelete() : setSure(true))} onBlur={() => setSure(false)}
        className={`rounded-full px-3 py-1.5 text-[11px] transition-colors duration-150 ${sure ? "bg-rose-500 text-white" : "text-white/35 hover:text-rose-300"}`}>
        {sure ? "Remove?" : <Trash2 className="h-4 w-4" />}
      </button>
    </li>
  );
}

/** The Recording Studio home: setup status, new session, every saved session. */
export default function SessionsHome() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(loadSetup);
  const [wizard, setWizard] = useState(false);
  useEffect(() => { listRecSessions().then(setRows).catch((e) => { setRows([]); toast.error("Couldn't load your sessions", { description: safeApiError(e) }); }); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const s = await createRecSession({ title: `Recording · ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}` });
      navigate(`/create/studio/${s.id}${setup.ready ? "" : "?setup=1"}`);
    } catch (e) { toast.error("Couldn't start a session", { description: safeApiError(e) }); setBusy(false); }
  };
  const remove = async (id) => {
    try { await deleteRecSession(id); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Session removed", { description: "Its takes are still in your Recordings." }); }
    catch (e) { toast.error("Couldn't remove it", { description: safeApiError(e) }); }
  };

  if (wizard) {
    return (
      <CreatorShell testId="rec-studio-setup-page" back="/create/studio" title="Recording setup" subtitle="Step 1: your microphone. Step 2: headphones, test sound and sync. Then you're ready.">
        <SetupWizard onDone={(next) => { if (next) setSetup(next); setWizard(false); }} />
      </CreatorShell>
    );
  }
  return (
    <CreatorShell testId="rec-studio-home" back="/create" wide title="ELPM Advanced Studio" subtitle="Smart recording — set up your mic once, then sing over any music with real effects, an automatic take check and WAV / MP3 export.">
      <SetupSummary setup={setup} onSetup={() => setWizard(true)} />
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button type="button" data-testid="rec-new-session" onClick={create} disabled={busy}
          className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-rose-500 to-red-600 px-8 py-4 font-display text-base font-bold shadow-[0_14px_44px_rgba(244,63,94,0.4)] transition-transform duration-200 hover:scale-[1.03] disabled:opacity-50">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />} New recording session
        </button>
        <Link to="/create/record" data-testid="rec-quick-record" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-[13px] text-white/80 transition-colors hover:bg-white/[0.08] hover:text-white">
          Quick record over a song <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/studio" data-testid="rec-advanced-mode" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-[13px] text-white/80 transition-colors hover:bg-white/[0.08] hover:text-white">
          <SlidersHorizontal className="h-4 w-4" /> Advanced mode (64-channel studio)
        </Link>
      </div>
      <p className="mb-3 mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">Your recording sessions</p>
      {rows === null ? <Loader2 className="h-5 w-5 animate-spin text-white/60" /> : rows.length === 0 ? (
        <p data-testid="rec-sessions-empty" className="text-white/55">No sessions yet — start your first one above.</p>
      ) : (
        <ul data-testid="rec-sessions-list" className="grid gap-3 md:grid-cols-2">
          {rows.map((s) => <SessionCard key={s.id} s={s} onOpen={() => navigate(`/create/studio/${s.id}`)} onDelete={() => remove(s.id)} />)}
        </ul>
      )}
    </CreatorShell>
  );
}
